import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '@/hooks/useTelegram';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/services/api';
import { CLASS_DATA, RACE_NAMES, type Character, type CharacterClass, type Race } from '@/types';

interface CharactersResponse {
  characters: Character[];
  count: number;
  maxCharacters: number;
  canCreate: boolean;
}

export function CharacterSelectPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();
  const { refreshCharacter } = useAuth();
  const [characters, setCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [canCreate, setCanCreate] = useState(true);
  const [maxCharacters, setMaxCharacters] = useState(5);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    loadCharacters();
  }, []);

  const loadCharacters = async () => {
    try {
      const data = await api.get<CharactersResponse>('/characters');
      setCharacters(data.characters);
      setCanCreate(data.canCreate);
      setMaxCharacters(data.maxCharacters);
    } catch (err) {
      console.error('Failed to load characters:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCharacter = async (characterId: string) => {
    hapticFeedback('medium');
    setSelecting(characterId);

    try {
      await api.post(`/characters/${characterId}/select`);
      await refreshCharacter();
      navigate('/adventure');
    } catch (err) {
      console.error('Failed to select character:', err);
      hapticFeedback('error');
    } finally {
      setSelecting(null);
    }
  };

  const handleDeleteCharacter = async (characterId: string) => {
    hapticFeedback('medium');
    setDeleting(characterId);
    setShowDeleteConfirm(null);

    try {
      await api.delete(`/characters/${characterId}`);
      // Immediately update local state for instant feedback
      setCharacters(prev => prev.filter(c => c.id !== characterId));
      setCanCreate(true); // User can now create more characters
      await refreshCharacter();
      hapticFeedback('success');
    } catch (err) {
      console.error('Failed to delete character:', err);
      hapticFeedback('error');
      // Reload on error to sync state
      await loadCharacters();
    } finally {
      setDeleting(null);
    }
  };

  const handleCreateNew = () => {
    hapticFeedback('medium');
    navigate('/character-creation');
  };

  const handleBack = () => {
    hapticFeedback('light');
    navigate('/mode-select');
  };

  const getClassEmoji = (charClass: CharacterClass | null): string => {
    const emojis: Record<CharacterClass, string> = {
      bard: '',
      barbarian: '',
      fighter: '',
      wizard: '',
      thief: '',
      immolator: '',
      druid: '',
      cleric: '',
      paladin: '',
      ranger: '',
    };
    return charClass ? emojis[charClass] || '' : '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="p-4 flex items-center justify-between border-b border-gray-800">
        <button
          onClick={handleBack}
          className="text-gray-400 hover:text-white transition-colors"
        >
          ← Назад
        </button>
        <h1 className="text-lg font-bold text-white">Персонажи</h1>
        <div className="text-gray-500 text-sm">
          {characters.length}/{maxCharacters}
        </div>
      </div>

      {/* Character List */}
      <div className="flex-1 p-4 space-y-3 overflow-y-auto">
        {characters.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 mb-4">У вас пока нет персонажей</p>
            <button
              onClick={handleCreateNew}
              className="px-6 py-3 bg-amber-500 text-black font-medium rounded-xl hover:bg-amber-400 transition-colors"
            >
              Создать первого персонажа
            </button>
          </div>
        ) : (
          <>
            {characters.map((char, index) => (
              <motion.div
                key={char.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative bg-gray-800 rounded-xl p-4 border border-gray-700"
              >
                <div className="flex items-start gap-3">
                  {/* Avatar/Class Icon */}
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-600/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-3xl">
                      {getClassEmoji(char.characterClass)}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white truncate">
                      {char.name || 'Безымянный'}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {char.characterClass ? CLASS_DATA[char.characterClass]?.nameRu : 'Не выбран класс'}
                      {char.race && ` • ${RACE_NAMES[char.race as Race] || char.race}`}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>Ур. {char.level}</span>
                      <span>HP: {char.currentHp || char.maxHp}/{char.maxHp}</span>
                      {(char.gold !== undefined || char.silver !== undefined || char.copper !== undefined) && (
                        <span>{char.copper ?? 0}м {char.silver ?? 0}с {char.gold ?? 0}з</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => handleSelectCharacter(char.id)}
                      disabled={selecting === char.id || deleting === char.id}
                      className="px-4 py-2 bg-amber-500 text-black text-sm font-medium rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
                    >
                      {selecting === char.id ? '...' : 'Играть'}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(char.id)}
                      disabled={selecting === char.id || deleting === char.id}
                      className="px-4 py-2 bg-gray-700 text-red-400 text-sm rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                    >
                      {deleting === char.id ? '...' : 'Удалить'}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Create New Button */}
            {canCreate && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: characters.length * 0.1 }}
                onClick={handleCreateNew}
                className="w-full p-4 rounded-xl border-2 border-dashed border-gray-700 text-gray-400 hover:border-amber-500/50 hover:text-amber-500 transition-all flex items-center justify-center gap-2"
              >
                <span className="text-xl">+</span>
                <span>Создать нового персонажа</span>
              </motion.button>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-gray-800 rounded-2xl p-6 max-w-sm w-full"
            >
              <h3 className="text-lg font-bold text-white mb-2 text-center">
                Удалить персонажа?
              </h3>
              <p className="text-gray-400 text-sm mb-6 text-center">
                Персонаж будет удалён безвозвратно. Все его приключения и прогресс будут потеряны.
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => handleDeleteCharacter(showDeleteConfirm)}
                  className="w-full py-3 px-4 rounded-xl bg-red-600 text-white font-medium hover:bg-red-500 transition-colors"
                >
                  Да, удалить
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="w-full py-2.5 rounded-xl bg-gray-700 text-gray-300 font-medium hover:bg-gray-600 transition-colors"
                >
                  Отмена
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
